import { useNavigate } from 'react-router-dom'
import { styled } from '@linaria/react'
import { MinimalButton, MinimalCard } from '@ovasabi/ui-minimal'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { CheckIcon } from '../../components/icons'
import { kitchenSetupSteps, type KitchenSetupInput } from './kitchenSetup'

const Style = {
  Head: styled.div`
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: ${minimalVars.space.xs};
    h2 { margin: 0; font-size: ${minimalVars.typography.bodySize}; }
    span { color: ${minimalVars.color.textSecondary}; font-size: ${minimalVars.typography.captionSize}; }
  `,
  List: styled.ol`
    list-style: none;
    margin: ${minimalVars.space.xs} 0 0;
    padding: 0;
    display: grid;
    gap: 2px;
  `,
  Item: styled.li<{ $done: boolean }>`
    display: grid;
    grid-template-columns: 24px minmax(0, 1fr) auto;
    align-items: center;
    gap: ${minimalVars.space.xs};
    min-height: 44px;
    color: ${({ $done }) => ($done ? minimalVars.color.textTertiary : minimalVars.color.textPrimary)};
    small { display: block; color: ${minimalVars.color.textTertiary}; }
  `,
  Mark: styled.span<{ $done: boolean }>`
    display: grid;
    place-items: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 2px solid ${({ $done }) => ($done ? minimalVars.color.brand : minimalVars.color.borderStrong)};
    background: ${({ $done }) => ($done ? minimalVars.color.brand : 'transparent')};
    color: #fff;
  `,
}

/**
 * "Get your kitchen live": the few things a new chef must do before a customer
 * can order, in the order that unblocks the most. Each row opens the screen
 * that finishes it. The card disappears once every step is done.
 */
export const KitchenSetupChecklist = (input: KitchenSetupInput) => {
  const navigate = useNavigate()
  const steps = kitchenSetupSteps(input)
  const done = steps.filter((step) => step.done).length
  if (steps.length === 0 || done === steps.length) return null
  const next = steps.find((step) => !step.done)
  return (
    <MinimalCard padding="sm" enter={false} aria-label="Kitchen setup">
      <Style.Head>
        <h2>Get your kitchen live</h2>
        <span>{done} of {steps.length} done</span>
      </Style.Head>
      <Style.List>
        {steps.map((step) => (
          <Style.Item key={step.key} $done={step.done}>
            <Style.Mark $done={step.done} aria-hidden="true">{step.done ? <CheckIcon size="sm" /> : null}</Style.Mark>
            <span>
              {step.title}
              {!step.done && <small>{step.hint}</small>}
            </span>
            {!step.done && (
              <MinimalButton size="sm" variant={step === next ? 'primary' : 'ghost'} onClick={() => navigate(step.to)}>
                {step.action}
              </MinimalButton>
            )}
          </Style.Item>
        ))}
      </Style.List>
    </MinimalCard>
  )
}
